import * as XLSX from "xlsx";
import { frequencies, normalizeCategory, priorities } from "../constants";
import { parseCsvExpenses } from "./csv";
import { createId, readFileAsArrayBuffer, readFileAsText } from "./formatters";

const getExtension = (file) => file.name.split(".").pop().toLowerCase();

const getValue = (row, keys) => {
  const lowerMap = Object.keys(row).reduce((map, key) => {
    map[key.toLowerCase()] = row[key];
    return map;
  }, {});

  for (const key of keys) {
    const value = row[key] ?? lowerMap[key.toLowerCase()];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
};

const normalizeTags = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || "")
    .split(/[|,]/)
    .map((tag) => tag.trim())
    .filter(Boolean);
};

const normalizeRows = (rows, userId, fallbackCurrency) =>
  rows
    .map((row) => {
      const title = String(getValue(row, ["title", "name", "expense", "transaction"])).trim();
      const amount = Number(getValue(row, ["amount", "value", "price", "sum"]));
      const rawDate = getValue(row, ["date", "created", "createdAt"]);
      const date = rawDate ? new Date(rawDate) : new Date();
      const category = normalizeCategory(getValue(row, ["category", "type"]));
      const rawFrequency = String(getValue(row, ["frequency", "repeat"]) || "One-time");
      const rawPriority = String(getValue(row, ["priority"]) || "Medium");
      const currency = String(getValue(row, ["currency"]) || fallbackCurrency).toUpperCase();

      if (!title || !amount || Number.isNaN(date.getTime())) {
        return null;
      }

      return {
        id: createId(),
        title,
        amount,
        date: date.toISOString(),
        category,
        description: String(getValue(row, ["description", "note", "notes"]) || ""),
        tags: normalizeTags(getValue(row, ["tags", "tag"])),
        frequency: frequencies.includes(rawFrequency) ? rawFrequency : "One-time",
        priority: priorities.includes(rawPriority) ? rawPriority : "Medium",
        currency: currency || fallbackCurrency,
        userId,
        receiptName: "",
        receiptDataUrl: "",
        receiptType: "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    })
    .filter(Boolean);

export const importExpensesFromFile = async (file, userId, fallbackCurrency) => {
  const extension = getExtension(file);

  if (extension === "csv") {
    return parseCsvExpenses(await readFileAsText(file), userId, fallbackCurrency);
  }

  if (extension === "json") {
    const parsed = JSON.parse(await readFileAsText(file));
    const rows = Array.isArray(parsed) ? parsed : parsed.expenses;
    return normalizeRows(Array.isArray(rows) ? rows : [], userId, fallbackCurrency);
  }

  if (["xlsx", "xls"].includes(extension)) {
    const workbook = XLSX.read(await readFileAsArrayBuffer(file), { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
    return normalizeRows(rows, userId, fallbackCurrency);
  }

  throw new Error("Supported import formats: CSV, Excel, JSON.");
};

export const expensesForExport = (expenses) =>
  expenses.map((expense) => ({
    title: expense.title,
    amount: expense.amount,
    currency: expense.currency,
    amountConverted: expense.amountConverted,
    category: expense.category,
    date: expense.date,
    priority: expense.priority,
    frequency: expense.frequency,
    tags: (expense.tags || []).join("|"),
    description: expense.description || "",
    receiptName: expense.receiptName || ""
  }));
